import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["formError", "listSection"];
  static values = { max: Number };

  connect() {
    this.boundHandleSubmit = this.handleSubmit.bind(this);
    this.boundClearError = this.clearError.bind(this);
    this.element.addEventListener("submit", this.boundHandleSubmit);
    this.element.addEventListener("change", this.boundClearError);

    if (this.element.querySelector("input[data-exclusive]")) {
      this.boundHandleExclusive = this.handleExclusive.bind(this);
      this.element.addEventListener("change", this.boundHandleExclusive);
    }
  }

  disconnect() {
    this.element.removeEventListener("submit", this.boundHandleSubmit);
    this.element.removeEventListener("change", this.boundClearError);
    if (this.boundHandleExclusive) {
      this.element.removeEventListener("change", this.boundHandleExclusive);
    }
  }

  handleSubmit(event) {
    // Buttons carrying this data attribute (back, previous, edit, "Add a pet/gift") jump to another
    // step directly rather than completing the current one, so they must bypass validation.
    if (event.submitter?.closest("[data-button-field-value-value]")) return;

    const error = this.hasListSectionTarget ? this.validateListSections() : this.validate(this.groupCheckboxesByName());

    if (error) {
      event.preventDefault();
      event.stopPropagation();
      this.formErrorTarget.textContent = error;
      this.formErrorTarget.classList.remove("hidden");
    } else {
      this.formErrorTarget.classList.add("hidden");
    }
  }

  clearError(event) {
    if (event.target.type !== "checkbox" && event.target.type !== "radio") return;
    this.formErrorTarget.classList.add("hidden");
  }

  // Validates "Yes/No + add at least one" sections (partner, children, pets, gifts, charities): if the
  // toggleable section is visible (i.e. "Yes" was selected) it must contain an added item or a checked checkbox.
  validateListSections() {
    for (const target of this.listSectionTargets) {
      if (!this.isElementVisible(target)) continue;
      const hasAddedItem = target.querySelector("[data-list-card]");
      const hasCheckedCheckbox = target.querySelector("input[type='checkbox']:checked");
      if (!hasAddedItem && !hasCheckedCheckbox) {
        return "Please add at least one before continuing.";
      }
    }
    return null;
  }

  isElementVisible(element) {
    let el = element;
    while (el) {
      if (getComputedStyle(el).display === "none") return false;
      el = el.parentElement;
    }
    return true;
  }

  handleExclusive(event) {
    if (event.target.type !== "checkbox" || !event.target.checked) return;
    const allCheckboxes = this.element.querySelectorAll("input[type='checkbox']");
    if (event.target.dataset.exclusive) {
      allCheckboxes.forEach((cb) => { if (cb !== event.target) cb.checked = false; });
    } else {
      allCheckboxes.forEach((cb) => { if (cb.dataset.exclusive) cb.checked = false; });
    }
  }

  groupCheckboxesByName() {
    const groups = {};
    this.element.querySelectorAll("input[type='checkbox']").forEach((checkbox) => {
      if (!this.isVisible(checkbox)) return;
      if (!groups[checkbox.name]) groups[checkbox.name] = [];
      groups[checkbox.name].push(checkbox);
    });
    return groups;
  }

  isVisible(element) {
    let el = element.parentElement;
    while (el) {
      if (getComputedStyle(el).display === "none") return false;
      el = el.parentElement;
    }
    return true;
  }

  validate(groups) {
    // No checkboxes anywhere in the form (visible or hidden by a toggle) means there's nothing to
    // pick from at all — e.g. a brand new will with no contacts added yet — which must also error,
    // since an empty `groups` object here would otherwise silently pass validation.
    const hasAnyCheckboxes = this.element.querySelectorAll("input[type='checkbox']").length > 0;
    if (!hasAnyCheckboxes) {
      return "Please add at least one option before continuing.";
    }

    for (const [, checkboxes] of Object.entries(groups)) {
      const checkedCount = checkboxes.filter((cb) => cb.checked).length;

      if (checkedCount === 0) {
        return "Please select at least one option.";
      }

      if (this.hasMaxValue && checkedCount > this.maxValue) {
        return `Please select no more than ${this.maxValue} option${this.maxValue === 1 ? "" : "s"}.`;
      }
    }
    return null;
  }
}
